import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Collapse,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { getAllRefunds, updateRefundStatus } from '../service/refundService';


const RefundManager = () => {
  const [refunds, setRefunds] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null); // track expanded row
  const [comments, setComments] = useState({}); // comments per refund
  const [itemApprovals, setItemApprovals] = useState({}); // track approved amounts

  useEffect(() => {
    fetchRefunds();
  }, []);

  const fetchRefunds = async () => {
    try {
      const res = await getAllRefunds();
      setRefunds(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleExpandToggle = (refundId) => {
    setExpandedRow(expandedRow === refundId ? null : refundId);
  };

  const handleItemAmountChange = (refundId, itemId, value) => {
    setItemApprovals((prev) => ({
      ...prev,
      [refundId]: {
        ...prev[refundId],
        [itemId]: value,
      },
    }));
  };

  const handleAction = async (id, action) => {
    try {
      const payload = {
        adminComments: comments[id] || "",
        refundItems: (refunds.find((r) => r.refundId === id)?.refundItems || []).map(
          (item) => ({
            refundItemId: item.refundItemId,
            orderItemId: item.orderItemId,
            itemApprovedAmount:
              itemApprovals[id]?.[item.refundItemId] ?? item.itemRequestedAmount,
          })
        ),
      };

      await updateRefundStatus(id, action, payload);

      fetchRefunds();
      setComments((prev) => ({ ...prev, [id]: "" }));
      setItemApprovals((prev) => ({ ...prev, [id]: {} }));
    } catch (err) {
      console.error(err);
    }
  };

  const refundColumns = [
    { field: "refundId", headerName: "ID", width: 80 },
    { field: "orderId", headerName: "Order", width: 120 },
    { field: "requestedAmount", headerName: "Requested", width: 120 },
    { field: "approvedAmount", headerName: "Approved", width: 120 },
    { field: "refundReason", headerName: "Reason", width: 180 },
    { field: "refundStatus", headerName: "Status", width: 150 },
    { field: "initiatedBy", headerName: "User", width: 150 },
    { field: "createdAt", headerName: "Requested At", width: 200 },
    {
      field: "expand",
      headerName: "",
      width: 80,
      renderCell: (params) => (
        <IconButton onClick={() => handleExpandToggle(params.row.refundId)}>
          {expandedRow === params.row.refundId ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 250,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField
            placeholder="Admin comments"
            size="small"
            value={comments[params.row.refundId] || ""}
            onChange={(e) =>
              setComments((prev) => ({
                ...prev,
                [params.row.refundId]: e.target.value,
              }))
            }
          />
          <Button
            variant="contained"
            color="success"
            onClick={() => handleAction(params.row.refundId, "approve")}
          >
            Approve
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => handleAction(params.row.refundId, "reject")}
          >
            Reject
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ maxWidth: "1000px", mx: "auto", mt: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Refund Manager (Admin)
        </Typography>
        <Box sx={{ height: 500, mt: 2 }}>
          <DataGrid
            rows={refunds}
            getRowId={(row) => row.refundId}
            columns={refundColumns}
            pageSizeOptions={[5, 10]}
            disableRowSelectionOnClick
          />
        </Box>

        {/* Expanded row details */}
        {expandedRow && (
          <Collapse in={true} timeout="auto" unmountOnExit>
            <Paper sx={{ mt: 2, p: 2 }}>
              <Typography variant="subtitle1">
                Refund Items for Refund #{expandedRow}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell>Variant</TableCell>
                    <TableCell>Quantity</TableCell>
                    <TableCell>Requested Amount</TableCell>
                    <TableCell>Approved Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {refunds
                    .find((r) => r.refundId === expandedRow)
                    ?.refundItems?.map((item) => (
                      <TableRow key={item.refundItemId}>
                        <TableCell>{item.productName}</TableCell>
                        <TableCell>{item.productVariantName}</TableCell>
                        <TableCell>{item.quantity}</TableCell>
                        <TableCell>{item.itemRequestedAmount}</TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            defaultValue={item.itemRequestedAmount}
                            onChange={(e) =>
                              handleItemAmountChange(
                                expandedRow,
                                item.refundItemId,
                                Number(e.target.value)
                              )
                            }
                            inputProps={{ min: 0 }}
                            style={{ width: "100px" }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </Paper>
          </Collapse>
        )}
      </Paper>
    </Box>
  );
};

export default RefundManager;